import { GridColDef, GridSortModel, GridCellParams } from '@mui/x-data-grid';
import { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { DataGridModel, DataGrid } from 'src/shared/components/organisms/dataGrid';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { Info } from '@mui/icons-material';
import { getEmailsByMission, InvoiceMailStatusFilter } from 'src/shared/services/emailService';
import { Box } from '@mui/material';
import { EmailResponse } from 'src/modules/groupMessageEmail/models/emailResponse';
import { GroupMessageEmailStatuses } from 'src/modules/groupMessageEmail/components/groupMessageEmailStatuses';

type InvoiceMailDeliveryStatusDataGridProps = {
  missionId: number;
  sendingType: InvoiceMailStatusFilter['sendingType'];
};

export const InvoiceMailDeliveryStatusDataGrid = (props: InvoiceMailDeliveryStatusDataGridProps) => {
  const { missionId, sendingType } = props;
  const { t } = useTranslation();
  const isMounted = useIsMounted();
  const [rows, setRows] = useState<EmailResponse[]>([]);
  const [data, setData] = useState<DataGridModel>({
    loading: true,
    totalRows: 0,
    rowsPerPageOptions: [5, 10, 15, 25, 50, 100],
    pageSize: 25,
    page: 0,
  });

  const [sortModel, setSortModel] = useState<GridSortModel>([{ field: 'recipientEmail', sort: 'asc' }]);

  const updateData = (key: keyof DataGridModel, value) => setData((prev) => ({ ...prev, [key]: value }));

  const onPageChange = (newPageIndex: number) => updateData('page', newPageIndex);
  const onPageSizeChange = (newPageSize: number) => {
    updateData('page', 0);
    updateData('pageSize', newPageSize);
  };

  const fetchData = useCallback(async () => {
    if (!isMounted()) return;
    updateData('loading', true);
    try {
      const result = await getEmailsByMission({
        pageIndex: data.page,
        pageSize: data.pageSize,
        sortCol: sortModel[0].field,
        sortDir: (sortModel[0].sort ?? 'asc') as string,
        missionId,
        sendingType,
      });
      if (isMounted()) {
        updateData('totalRows', result.data.totalItems);
        setRows(result.data.results);
      }
    } catch (e) {
      if (isMounted()) ConsoleHelper.log(e);
    } finally {
      if (isMounted()) updateData('loading', false);
    }
  }, [data.page, data.pageSize, isMounted, missionId, sendingType, sortModel]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const columns: GridColDef[] = [
    {
      field: 'recipientEmail',
      headerName: t('groupMessage:email:colHeads.email'),
      sortable: true,
      flex: 2,
      renderCell: (params: GridCellParams) => <>{params.row.recipientEmail}</>,
    },
    {
      field: 'sent',
      headerName: t('groupMessage:email:colHeads.status'),
      sortable: true,
      flex: 1,
      renderCell: (params: GridCellParams) => {
        const { sent } = params.row;
        return (
          <Box py={0.5}>
            <GroupMessageEmailStatuses chipSize='small' sent={sent} />
          </Box>
        );
      },
    },
    {
      field: 'openCount',
      headerName: t('groupMessage:email:colHeads.openCount'),
      sortable: false,
      flex: 1,
      renderCell: (params: GridCellParams) => <>{params.row.openCount ?? ''}</>,
    },
    {
      field: 'clickCount',
      headerName: t('groupMessage:email:colHeads.clickCount'),
      sortable: false,
      flex: 1,
      renderCell: (params: GridCellParams) => <>{params.row.clickCount ?? ''}</>,
    },
    {
      field: 'error',
      headerName: t('groupMessage:email:colHeads.error'),
      sortable: true,
      flex: 1.5,
      renderCell: (params: GridCellParams) => (
        <GroupMessageEmailStatuses chipSize='small' error={params.row.error} errorLabel={params.row.errorMsg ?? undefined} />
      ),
    },
    {
      field: 'mail_id',
      headerName: '',
      sortable: false,
      flex: 0.1,
      renderCell: () => <Info fontSize='small' />,
    },
  ];

  return (
    <DataGrid<EmailResponse>
      columns={columns}
      rows={rows}
      autoHeight
      columnVisibilityModel={{ mail_id: false }}
      getRowId={(row) => row.mail_id}
      dataModel={data}
      checkboxSelection={false}
      disableSelectionOnClick
      onPageChange={onPageChange}
      onPageSizeChange={onPageSizeChange}
      sortModel={sortModel}
      onSortModelChange={(newSortModel) => {
        if (newSortModel.length) {
          setSortModel(newSortModel);
        } else {
          setSortModel([{ field: sortModel[0].field, sort: sortModel[0].sort === 'asc' ? 'desc' : 'asc' }]);
        }
      }}
      selectionModel={[]}
      onSelectionModelChange={() => ConsoleHelper.log('')}
    />
  );
};
